import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import type { FilterValue } from '@/lib/types/filter'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { IssueCommandInput } from '@/components/app/combo-box/IssueCommandInput'
import { cn } from '@/lib/utils'

export interface MultiSelectOption {
  value: FilterValue
  label: string
  icon?: React.ReactNode
}

interface MultiSelectCommandListProps {
  options: Array<MultiSelectOption>
  selectedValues: Array<FilterValue>
  onToggle: (value: FilterValue) => void
  searchPlaceholder: string
  emptyMessage?: string
  /** Tasks per value, shown beside each option (design 9b). */
  counts?: Map<FilterValue, number> | null
  /** Shortcuts under the list, e.g. Any open · Any closed. */
  footer?: React.ReactNode
}

export function MultiSelectCommandList({
  options,
  selectedValues,
  onToggle,
  searchPlaceholder,
  emptyMessage,
  counts,
  footer,
}: MultiSelectCommandListProps) {
  const { t } = useTranslation('tasks')
  return (
    <Command>
      <IssueCommandInput placeholder={searchPlaceholder} />
      <CommandList>
        <CommandEmpty>{emptyMessage ?? t('filters.noResults')}</CommandEmpty>
        <CommandGroup>
          {options.map((opt) => {
            const isSelected = selectedValues.includes(opt.value)
            return (
              <CommandItem
                key={String(opt.value)}
                value={opt.label}
                onSelect={() => onToggle(opt.value)}
                className="flex items-center gap-2 text-xs"
              >
                <span className="flex items-center gap-2 flex-1">
                  {opt.icon}
                  {opt.label}
                </span>
                {counts && (
                  <span className="text-[11px] tabular-nums text-muted-foreground">
                    {counts.get(opt.value) ?? 0}
                  </span>
                )}
                <Check
                  className={cn('size-3.5 shrink-0 text-primary', !isSelected && 'invisible')}
                />
              </CommandItem>
            )
          })}
        </CommandGroup>
      </CommandList>
      {footer}
    </Command>
  )
}
